import { pick } from 'underscore';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { isMSANServingDNV, ScopeLevel } from '@bingads-webui-campaign-react/constants';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { modelConstraints, ERROR_ENTITY, isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { bulkModelToViewModelForAdGroup, viewModelToModelForAdGroup } from './ad-group';
import { viewModelToBulkModelForLocationTargets, viewModelToBulkModelForAdSchedules, viewModelToBulkModelForTargeting, viewModelToBulkModelForAudiences, viewModelToBulkModelForPlacements } from './ad-group-criterions';

const adGroupEDM = edmCampaign.types.resolve('Model.AdGroup');
const adGroupResource = new adGroupEDM.ResourceIdentifier();

const createOrUpdateAdGroup = (data) => {
  const createOrUpdateCall = data && data.Id ?
    adGroupResource.bulkUpdate.bind(adGroupResource) :
    adGroupResource.bulkCreate.bind(adGroupResource);
  return createOrUpdateCall(data);
};

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

const bulkUpsertAdGroupSettingsEntityErrorMap = {
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
};

const parallelSave = async (modelToSave, adGroupViewModel, campaign) => {
  const savePromise = createOrUpdateAdGroup(modelToSave);

  let saveExclusionsPromise = Promise.resolve();
  if (adGroupViewModel && adGroupViewModel.data && adGroupViewModel.data.exclusions) {
    const saveExclusionsData = {
      CampaignId: campaign.Id,
      AdGroupId: adGroupViewModel.data.Id,
      AdvancedSettings: {
        ExcludedSites: adGroupViewModel.data.exclusions.adgroupExclusions.ExcludedSites,
      },
    };
    saveExclusionsPromise = exclusionRepository.saveExtensions(saveExclusionsData, ScopeLevel.AdGroup);
  }

  return {
    result: await savePromise,
    resultExclusion: await saveExclusionsPromise,
  };
};

export const getErrorsPlaceholders = () => ({
  systemErrors: [],
  serverErrors: [],
});

export const saveAdGroup = async ({
  adGroup: adGroupViewModel,
  bundleCountries,
  bundleLocationCriterions,
  campaign,
  customTargetingBulkModel = null,
  isDisplayPlus = false,
  flow = '',
}, {
  scenario, i18n, activityLogger, serverContext,
}) => {
  // NOTE: adGroupViewModel.data.CampaignBidStrategyName is not set when this is called first time from create-ad step.
  // ensure that you have no logic relying on this or any other fields being set from campaign model onto adgroup model.

  const adGroupModel = viewModelToModelForAdGroup({ adGroupViewModel, i18n, isDisplayPlus });
  const locationTargetsModel = viewModelToBulkModelForLocationTargets({
    bundleLocationCriterions,
    adGroupViewModel,
    campaignViewModel: campaign,
  });
  const adSchedulesModel = viewModelToBulkModelForAdSchedules({ adGroupViewModel, campaignViewModel: campaign });
  let isMSANServingDNVCampaign = false;
  if (flow === 'VIDEO_CAMPAIGNS' && campaign.CampaignSubType === 4 && serverContext.DynamicPermissions.EnableMSANVideoCampaigns) {
    isMSANServingDNVCampaign = true;
  } else if (flow === 'DISPLAY_CAMPAIGNS' && campaign.CampaignSubType === 5 && serverContext.DynamicPermissions.EnableMSANDisplayCampaigns) {
    isMSANServingDNVCampaign = true;
  } else {
    isMSANServingDNVCampaign = isMSANServingDNV(campaign.CampaignSubType, campaign.CampaignSettings);
  }

  const targetingModel = customTargetingBulkModel ?
    customTargetingBulkModel({ adGroupViewModel, campaignViewModel: campaign }) :
    viewModelToBulkModelForTargeting({
      adGroupViewModel,
      campaignViewModel: campaign,
      isXandrDeviceTargetEnabled: serverContext.DynamicPermissions.XandrDeviceTarget,
      isBoostEnabled: serverContext.Permissions.IsAmplifyEnabled,
      isDeviceTargetingForDisplayEnabled: serverContext.DynamicPermissions.DeviceTargetingForDisplay,
      isMultiAdFormatEnabled: serverContext.DynamicPermissions.MultiAdFormat,
      isPremiumStreamingEnabled: serverContext.DynamicPermissions.PremiumStreaming,
      isXandrServing: !isMSANServingDNVCampaign,
    });
  const audienceCriterionsModel = viewModelToBulkModelForAudiences({ adGroupViewModel, campaignViewModel: campaign });

  const placementCriterionsModel = viewModelToBulkModelForPlacements({ adGroupViewModel, campaignViewModel: campaign });

  const modelToSave = {
    ...adGroupModel,
    AdGroupCriterions: [
      ...locationTargetsModel,
      ...adSchedulesModel,
      ...targetingModel,
      ...placementCriterionsModel,
    ],
    AdGroupAudienceCriterions: audienceCriterionsModel,
    Campaign: pick(campaign, 'Id'),
  };

  try {
    const saveResult = await parallelSave(modelToSave, adGroupViewModel, campaign);

    const data = {
      adGroup: bulkModelToViewModelForAdGroup({
        adGroupModel: saveResult.result,
        originalAdGroupViewModel: adGroupViewModel,
        bundleCountries,
        i18n,
      }),
      ...getErrorsPlaceholders(),
    };
    return data;
  } catch (bulkOrSystemError) {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { entity, errors } = bulkOrSystemError;
      const adGroup = entity ?
        bulkModelToViewModelForAdGroup({
          adGroupModel: entity,
          originalAdGroupViewModel: adGroupViewModel,
          bundleCountries,
          errors,
          i18n,
        }) :
        adGroupViewModel;

      if (adGroupViewModel.data.deal) {
        adGroup.data = await modelConstraints(adGroupViewModel.data.deal, adGroupViewModel.data, serverContext);
      }

      let filteredErrors = errors;
      if (adGroup.data.Id <= 0) {
        filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
      }

      const serverErrors = modelToViewModelForBulkErrors(
        filteredErrors,
        bulkUpsertAdGroupSettingsEntityErrorMap,
        scenario,
        i18n,
        activityLogger
      );

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        adGroup,
        serverErrors,
      });
    }

    if (bulkOrSystemError && bulkOrSystemError.NegativeSitesResult) {
      const { NegativeSitesResult } = bulkOrSystemError;
      const serverErrors = [{
        property: 'webExclusions',
        NegativeSitesResult: NegativeSitesResult.EntityErrors,
      }];

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        adGroup: adGroupViewModel,
        serverErrors,
      });
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`AdGroup bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
    // eslint-disable-next-line prefer-promise-reject-errors

    return {
      adGroup: adGroupViewModel,
      systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
    };
  }
};

export const saveJustAdGroup = async ({
  adGroup: adGroupViewModel,
  campaign,
}, {
  i18n,
}) => {
  const adGroupModel = viewModelToModelForAdGroup({ adGroupViewModel, i18n });

  const modelToSave = {
    ...adGroupModel,
    Campaign: pick(campaign, 'Id'),
  };

  try {
    const result = await createOrUpdateAdGroup(modelToSave);
    const data = {
      adGroup: bulkModelToViewModelForAdGroup({
        adGroupModel: result,
        originalAdGroupViewModel: adGroupViewModel,
        i18n,
      }),
      ...getErrorsPlaceholders(),
    };
    return data;
  } catch (bulkOrSystemError) {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { errors } = bulkOrSystemError;

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        errors,
      });
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`AdGroup bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
    // eslint-disable-next-line prefer-promise-reject-errors

    return {
      adGroup: adGroupViewModel,
      systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
    };
  }
};
